import React, { useState, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { withAuth } from '../../hoc/withAuth';
import { useAuth } from '../../providers/AuthProvider';
import { Navigation } from '../../components/Navigation';
import { UserAvatar } from '../../components/UserAvatar';
import { Settings, Package, Camera } from 'lucide-react';
import {
  useGetData,
  useUpdateFirestoreItem,
  useUploadImages,
  useDeleteImages,
  useGetFirestoreItemsByQuery,
} from '../../hooks';
import {
  COLLECTIONS,
  COLLECTIONS_QUERIES,
  locationMessages,
  TABS,
} from '../../constants';
import { getQueryFilter } from '../../utils';
import { ServicesTab } from './ServicesTab';
import { SettingsTab } from './SettingsTab';
import { LayoutWrapper } from '../../components/LayoutWrapper';

// Types
export interface ProfileFormData {
  displayName: string;
  email: string;
  phoneNumber: string;
  telegram: string;
  location: string;
  bio: string;
}

type TabType = typeof TABS[keyof typeof TABS];

// Utility Components
const TabButton = ({
  activeTab,
  targetTab,
  onClick,
  icon: Icon,
  defaultMessage,
}: {
  activeTab: TabType;
  targetTab: TabType;
  onClick: () => void;
  icon: React.ElementType;
  defaultMessage: string;
}) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg flex items-center ${
      activeTab === targetTab
        ? 'bg-blue-600 text-white'
        : 'bg-white text-gray-600 hover:bg-gray-50'
    }`}
  >
    <Icon className='w-4 h-4 mr-2' />
    {defaultMessage}
  </button>
);

const UserProfileSkeleton = () => (
  <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
    <div className='flex items-start justify-between'>
      <div className='flex items-start space-x-6'>
        <div className='w-24 h-24 bg-gray-200 rounded-full animate-pulse' />
        <div className='space-y-2'>
          <div className='h-8 bg-gray-200 rounded w-48 animate-pulse' />
          <div className='h-4 bg-gray-200 rounded w-32 animate-pulse' />
          <div className='h-4 bg-gray-200 rounded w-40 animate-pulse' />
        </div>
      </div>
      <div className='w-6 h-6 bg-gray-200 rounded animate-pulse' />
    </div>
  </div>
);

// Main Component
const UserProfileComponent: React.FC = () => {
  const location = useLocation();
  const { formatMessage } = useIntl();
  const { activeTab: _activeTab } = location.state || {};
  const [activeTab, setActiveTab] = useState<TabType>(
    _activeTab || TABS.SERVICES,
  );
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>(
    {},
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  // Data fetching
  const { data: userData, isPending: isPendingUserData } = useGetData(
    user?.uid,
    COLLECTIONS.users,
  );

  const queryFilters = [
    getQueryFilter({
      field: COLLECTIONS_QUERIES.providerUuid,
      value: user?.uid,
    }),
  ];

  const { data: services, isPending: isPendingServices } =
    useGetFirestoreItemsByQuery(queryFilters);

  const sortedServices = useMemo(
    () =>
      services?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt)),
    [services],
  );

  // Mutations
  const { mutate: updateUserData } = useUpdateFirestoreItem();
  const { uploadImages } = useUploadImages({ setUploadProgress });
  const { mutate: deleteImages } = useDeleteImages();

  // Handlers
  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (
      !file ||
      !file.type.startsWith('image/') ||
      file.size > 3 * 1024 * 1024
    ) {
      alert(
        formatMessage({
          id: 'invalidFile',
          defaultMessage: 'Please select an image file under 5MB',
        }),
      );
      return;
    }

    try {
      const [url] = await uploadImages([file], COLLECTIONS.users);
      if (userData?.photoUrl?.includes(COLLECTIONS.users)) {
        deleteImages([userData.photoUrl]);
      }
      updateUserData({
        collection: COLLECTIONS.users,
        id: user?.uid,
        newData: { photoUrl: url },
      });
    } catch (error) {
      console.error('Photo upload error:', error);
      alert(
        formatMessage({
          id: 'uploadError',
          defaultMessage: 'Error uploading photo',
        }),
      );
    }
  };

  // User Profile Header
  const ProfileHeader = () => (
    <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
      <div className='flex items-start justify-between'>
        <div className='flex items-start space-x-6'>
          <div className='relative group'>
            <input
              type='file'
              ref={fileInputRef}
              className='hidden'
              accept='image/*'
              onChange={handlePhotoUpload}
            />
            <div
              className='relative w-24 h-24 rounded-full overflow-hidden cursor-pointer'
              onClick={() => fileInputRef.current?.click()}
            >
              <UserAvatar
                photoURL={userData?.photoUrl}
                displayName={userData?.displayName}
                email={userData?.email}
                size='lg'
              />
              <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full'>
                <Camera className='w-6 h-6 text-white' />
              </div>
              {uploadProgress[0] < 100 && (
                <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full'>
                  <div className='animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent'></div>
                </div>
              )}
            </div>
          </div>
          <div>
            <h1 className='text-2xl font-bold text-gray-900'>
              {userData?.displayName}
            </h1>
            {userData?.location && (
              <p className='text-gray-600'>
                {formatMessage(
                  locationMessages[`location${userData.location}`],
                )}
              </p>
            )}
            <p className='text-sm text-gray-500'>
              <FormattedMessage
                id='joinedDate'
                defaultMessage='На сайте с {joinedDate}'
                values={{
                  joinedDate: new Date(
                    userData?.createdAt?.seconds * 1000,
                  ).toLocaleDateString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  }),
                }}
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <LayoutWrapper>
      <div className='container mx-auto'>
        {isPendingUserData ? <UserProfileSkeleton /> : <ProfileHeader />}

        <div className='flex gap-4 mb-6'>
          <TabButton
            activeTab={activeTab}
            targetTab={TABS.SERVICES}
            onClick={() => setActiveTab(TABS.SERVICES)}
            icon={Package}
            defaultMessage={formatMessage({
              id: 'userProfile.myServices',
              defaultMessage: 'Мои услуги',
            })}
          />
          <TabButton
            activeTab={activeTab}
            targetTab={TABS.SETTINGS}
            onClick={() => setActiveTab(TABS.SETTINGS)}
            icon={Settings}
            defaultMessage={formatMessage({
              id: 'userProfile.profileSettings',
              defaultMessage: 'Настройки профиля',
            })}
          />
        </div>

        {activeTab === TABS.SERVICES ? (
          <ServicesTab
            userData={userData}
            services={sortedServices}
            isPending={isPendingServices}
            queryFilters={queryFilters}
          />
        ) : (
          <SettingsTab userData={userData} isPending={isPendingUserData} />
        )}
      </div>
    </LayoutWrapper>
  );
};

export const UserProfile = withAuth(UserProfileComponent);
